////////////////////////////////////////
// # GOLDEN RULE!!!! HONOR ABOVE ALL!!!!
////////////////////////////////////////
// NO NEW FILES!!!!!!!!!
////////////////////////////////////////

/*
░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
*/
// ##                                                                                                               REQUIREMENTS 'R'
/*
░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
R: BitsBound Sales Receptionist Subpod - Chat Modal Container
- Full-featured chat interface modal for BitsBound sales conversations
- Manages conversation state and API calls to backend
- Displays message history with Subpea component
- Welcome screen with quick start prompts for SaaS sales
- Resizable on desktop, full-screen on mobile
- BitsBound brand colors and styling
*/

// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════
// DVT_EIS_TP: THIRD-PARTY IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════

import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════
// DVT_EIS_FP: FIRST-PARTY IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════

import { Bitsbound_Kings_Home_Primary_SRC_Homepage_SubLevels_SalesReceptionist_Subpea } from '../subpea/Bitsbound_Kings_Home_Primary_SRC_Homepage_SubLevels_SalesReceptionist_Subpea.js';

// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════
// DVT_IIS: TYPES AND STYLED COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

// BitsBound Brand Colors
const BRAND = {
  primary: '#6366f1',
  primaryLight: '#818cf8',
  primaryDark: '#4f46e5',
  accent: '#22d3ee',
  background: '#0f172a',
  surface: '#1e293b',
  surfaceElevated: '#334155',
  text: '#f8fafc',
  textMuted: '#94a3b8',
  border: '#334155',
  success: '#22c55e',
  error: '#ef4444',
};

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const slideOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
`;

const pulseGlow = keyframes`
  0%, 100% {
    box-shadow: 0 4px 20px rgba(99, 102, 241, 0.4), 0 0 0 0 rgba(99, 102, 241, 0.4);
  }
  50% {
    box-shadow: 0 4px 25px rgba(99, 102, 241, 0.5), 0 0 0 8px rgba(99, 102, 241, 0);
  }
`;

const FloatingWrapper = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 16px;

  @media (max-width: 768px) {
    bottom: 16px;
    right: 16px;
  }
`;

const FABContainer = styled.div<{ $isOpen: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    display: ${props => props.$isOpen ? 'none' : 'flex'};
  }
`;

const FAB = styled.button<{ $isOpen: boolean }>`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: none;
  background: linear-gradient(135deg, ${BRAND.primary}, ${BRAND.primaryDark});
  color: white;
  font-size: 26px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(99, 102, 241, 0.4);
  transition: all 0.3s ease;
  animation: ${props => props.$isOpen ? 'none' : pulseGlow} 2s ease-in-out infinite;

  &:hover {
    transform: scale(1.08);
    box-shadow: 0 6px 25px rgba(99, 102, 241, 0.5);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 768px) {
    width: 56px;
    height: 56px;
    font-size: 24px;
  }
`;

const FABTooltip = styled.div<{ $isVisible: boolean }>`
  background: ${BRAND.surface};
  color: ${BRAND.text};
  padding: 10px 16px;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  border: 1px solid ${BRAND.border};
  opacity: ${props => props.$isVisible ? 1 : 0};
  transform: ${props => props.$isVisible ? 'translateX(0)' : 'translateX(10px)'};
  transition: opacity 0.2s ease, transform 0.2s ease;
  pointer-events: none;

  &::after {
    content: '';
    position: absolute;
    right: -6px;
    top: 50%;
    transform: translateY(-50%);
    border: 6px solid transparent;
    border-left-color: ${BRAND.surface};
  }

  @media (max-width: 768px) {
    font-size: 0.8rem;
    padding: 8px 12px;
  }
`;

const mobileSlideIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const ChatModal = styled.div<{ $width: number; $height: number }>`
  width: ${props => props.$width}px;
  height: ${props => props.$height}px;
  min-width: 380px;
  min-height: 450px;
  max-width: calc(100vw - 48px);
  max-height: calc(100vh - 100px);
  background: ${BRAND.background};
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6), 0 0 0 1px ${BRAND.border};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${slideIn} 0.3s ease-out;

  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100% !important;
    height: 100% !important;
    max-width: 100%;
    max-height: 100%;
    min-width: 0;
    min-height: 0;
    border-radius: 0;
    padding-top: env(safe-area-inset-top, 0);
    padding-bottom: env(safe-area-inset-bottom, 0);
    padding-left: env(safe-area-inset-left, 0);
    padding-right: env(safe-area-inset-right, 0);
    animation: ${mobileSlideIn} 0.15s ease-out;
  }
`;

const ResizeHandle = styled.div<{ $position: 'top' | 'left' | 'top-left' }>`
  position: absolute;
  z-index: 10;

  ${props => props.$position === 'top' && `
    top: 0;
    left: 20px;
    right: 20px;
    height: 6px;
    cursor: ns-resize;
  `}

  ${props => props.$position === 'left' && `
    left: 0;
    top: 20px;
    bottom: 20px;
    width: 6px;
    cursor: ew-resize;
  `}

  ${props => props.$position === 'top-left' && `
    top: 0;
    left: 0;
    width: 16px;
    height: 16px;
    cursor: nwse-resize;
  `}

  &:hover {
    background: rgba(99, 102, 241, 0.2);
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const ChatHeader = styled.div`
  background: linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.primaryDark} 100%);
  padding: 20px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  flex-shrink: 0;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  }

  @media (max-width: 768px) {
    padding: 16px 16px;
    padding-top: calc(16px + env(safe-area-inset-top, 0));
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderIcon = styled.div`
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    font-size: 1.1rem;
  }
`;

const HeaderTitle = styled.div`
  color: #ffffff;
  font-weight: 700;
  font-size: 1.05rem;
  letter-spacing: -0.01em;
`;

const HeaderSubtitle = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.75rem;
  margin-top: 2px;
  font-weight: 500;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;
  flex-shrink: 0;
  color: #ffffff;
  font-size: 18px;
  font-weight: 300;
  line-height: 1;

  &:hover {
    background: rgba(255, 255, 255, 0.35);
    border-color: rgba(255, 255, 255, 0.5);
  }

  &:active {
    background: rgba(255, 255, 255, 0.45);
  }

  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.4);
    font-size: 22px;
  }
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;

  @media (max-width: 768px) {
    padding: 12px;
    gap: 10px;
  }
`;

const MessageBubble = styled.div<{ $isUser: boolean }>`
  max-width: 85%;
  padding: 14px 18px;
  border-radius: 16px;
  font-size: 0.9375rem;
  line-height: 1.6;
  align-self: ${props => props.$isUser ? 'flex-end' : 'flex-start'};
  background: ${props => props.$isUser
    ? `linear-gradient(135deg, ${BRAND.primary}, ${BRAND.primaryDark})`
    : BRAND.surface};
  color: ${props => props.$isUser ? '#ffffff' : BRAND.text};
  border: ${props => props.$isUser ? 'none' : `1px solid ${BRAND.border}`};
  box-shadow: ${props => props.$isUser
    ? '0 4px 15px rgba(99, 102, 241, 0.3)'
    : '0 2px 8px rgba(0, 0, 0, 0.2)'};
  word-wrap: break-word;
  overflow-wrap: break-word;

  @media (max-width: 768px) {
    max-width: 90%;
    padding: 12px 14px;
    font-size: 0.9rem;
    border-radius: 14px;
  }
`;

const TypingIndicator = styled.div`
  display: flex;
  gap: 5px;
  padding: 14px 18px;
  background: ${BRAND.surface};
  border: 1px solid ${BRAND.border};
  border-radius: 16px;
  width: fit-content;

  span {
    width: 8px;
    height: 8px;
    background: ${BRAND.primary};
    border-radius: 50%;
    animation: bounce 1.4s infinite ease-in-out;

    &:nth-of-type(1) { animation-delay: -0.32s; }
    &:nth-of-type(2) { animation-delay: -0.16s; }
  }

  @keyframes bounce {
    0%, 80%, 100% { transform: scale(0); opacity: 0.4; }
    40% { transform: scale(1); opacity: 1; }
  }
`;

const InputContainer = styled.form`
  padding: 16px 20px;
  border-top: 1px solid ${BRAND.border};
  display: flex;
  gap: 12px;
  background: rgba(15, 23, 42, 0.8);
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 12px;
    padding-bottom: calc(12px + env(safe-area-inset-bottom, 0));
    gap: 10px;
  }
`;

const Input = styled.textarea`
  flex: 1;
  background: ${BRAND.surface};
  border: 1px solid ${BRAND.border};
  border-radius: 16px;
  padding: 14px 18px;
  color: ${BRAND.text};
  font-size: 0.9375rem;
  font-family: inherit;
  outline: none;
  transition: all 0.2s ease;
  resize: none;
  min-height: 48px;
  max-height: 120px;
  line-height: 1.4;

  &::placeholder {
    color: ${BRAND.textMuted};
  }

  &:focus {
    border-color: ${BRAND.primary};
    background: ${BRAND.surfaceElevated};
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
  }

  @media (max-width: 768px) {
    font-size: 16px;
    padding: 12px 14px;
    min-height: 44px;
    max-height: 100px;
    border-radius: 12px;
  }
`;

const SendButton = styled.button`
  background: linear-gradient(135deg, ${BRAND.primary}, ${BRAND.primaryDark});
  border: none;
  border-radius: 12px;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
  flex-shrink: 0;
  align-self: flex-end;
  color: #ffffff;
  font-size: 20px;
  line-height: 1;

  &:hover:not(:disabled) {
    box-shadow: 0 6px 20px rgba(99, 102, 241, 0.4);
  }

  &:active:not(:disabled) {
    background: linear-gradient(135deg, ${BRAND.primaryDark}, ${BRAND.primary});
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
    font-size: 18px;
    border-radius: 10px;
  }
`;

const WelcomeSection = styled.div`
  padding: 8px;

  @media (max-width: 768px) {
    padding: 4px;
  }
`;

const WelcomeHeader = styled.div`
  text-align: center;
  padding: 16px 16px 20px;
  background: linear-gradient(180deg, rgba(99, 102, 241, 0.1) 0%, transparent 100%);
  border-radius: 12px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    padding: 12px 12px 16px;
    margin-bottom: 12px;
  }
`;

const WelcomeIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 12px;

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 8px;
  }
`;

const WelcomeTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${BRAND.text};
  margin: 0 0 8px;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin: 0 0 6px;
  }
`;

const WelcomeSubtitle = styled.p`
  font-size: 0.85rem;
  color: ${BRAND.textMuted};
  margin: 0;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const ProductBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(99, 102, 241, 0.15);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 20px;
  padding: 6px 14px;
  font-size: 0.75rem;
  color: ${BRAND.primaryLight};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    padding: 5px 12px;
    font-size: 0.7rem;
    margin-bottom: 12px;
  }
`;

const InfoBox = styled.div`
  background: ${BRAND.surface};
  border: 1px solid ${BRAND.border};
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    padding: 12px;
    margin-bottom: 12px;
  }
`;

const InfoTitle = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${BRAND.accent};
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  @media (max-width: 768px) {
    font-size: 0.75rem;
    margin-bottom: 8px;
  }
`;

const InfoList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (max-width: 768px) {
    gap: 6px;
  }
`;

const InfoItem = styled.li`
  font-size: 0.8rem;
  color: ${BRAND.textMuted};
  display: flex;
  align-items: flex-start;
  gap: 8px;
  line-height: 1.4;

  &::before {
    content: '✓';
    color: ${BRAND.success};
    font-size: 0.7rem;
    margin-top: 2px;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    font-size: 0.75rem;
    gap: 6px;
  }
`;

const QuickStartSection = styled.div`
  margin-top: 8px;

  @media (max-width: 768px) {
    margin-top: 6px;
  }
`;

const QuickStartLabel = styled.div`
  font-size: 0.75rem;
  color: ${BRAND.textMuted};
  margin-bottom: 10px;
  text-align: center;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 0.7rem;
    margin-bottom: 8px;
  }
`;

const QuickStartGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (max-width: 768px) {
    gap: 6px;
  }
`;

const QuickStartButton = styled.button`
  background: ${BRAND.surface};
  border: 1px solid ${BRAND.border};
  border-radius: 10px;
  padding: 12px 14px;
  color: ${BRAND.text};
  font-size: 0.85rem;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;
  display: flex;
  align-items: center;
  gap: 10px;

  &:hover {
    background: ${BRAND.surfaceElevated};
    border-color: ${BRAND.primary};
  }

  &:active {
    background: rgba(99, 102, 241, 0.15);
  }

  @media (max-width: 768px) {
    padding: 14px 12px;
    font-size: 0.8rem;
    gap: 8px;
    min-height: 48px;
  }
`;

const QuickStartIcon = styled.span`
  font-size: 1.1rem;
  flex-shrink: 0;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

/*
████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████
*/
// ##                                                                                                               OUTPUTS 'O'
/*
████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████████
*/

// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════
// O_1: CHAT API CONFIG
// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3200';

// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════
// O_2: SUBPOD COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════

export interface Bitsbound_SalesReceptionistSubpodProps {
  onClose?: () => void;
}

const DEFAULT_WIDTH = 520;
const DEFAULT_HEIGHT = 700;
const MIN_WIDTH = 380;
const MIN_HEIGHT = 450;

export function Bitsbound_Kings_Home_Primary_SRC_Homepage_SubLevels_SalesReceptionist_Subpod({
  onClose
}: Bitsbound_SalesReceptionistSubpodProps): React.ReactElement {
  // Floating widget state
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Toggle chat open/close with animation
  const handleToggle = useCallback(() => {
    if (isOpen) {
      setIsClosing(true);
      setTimeout(() => {
        setIsOpen(false);
        setIsClosing(false);
        onClose?.();
      }, 250);
    } else {
      setIsOpen(true);
    }
  }, [isOpen, onClose]);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
      onClose?.();
    }, 250);
  }, [onClose]);

  // Resize state
  const [dimensions, setDimensions] = useState({ width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT });
  const [isResizing, setIsResizing] = useState<'top' | 'left' | 'top-left' | null>(null);
  const resizeStartRef = useRef({ x: 0, y: 0, width: 0, height: 0 });

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Resize handlers
  const handleResizeStart = useCallback((e: React.MouseEvent, position: 'top' | 'left' | 'top-left') => {
    e.preventDefault();
    setIsResizing(position);
    resizeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      width: dimensions.width,
      height: dimensions.height,
    };
  }, [dimensions]);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = resizeStartRef.current.x - e.clientX;
      const deltaY = resizeStartRef.current.y - e.clientY;

      setDimensions(prev => {
        let newWidth = prev.width;
        let newHeight = prev.height;

        if (isResizing === 'left' || isResizing === 'top-left') {
          newWidth = Math.max(MIN_WIDTH, resizeStartRef.current.width + deltaX);
        }
        if (isResizing === 'top' || isResizing === 'top-left') {
          newHeight = Math.max(MIN_HEIGHT, resizeStartRef.current.height + deltaY);
        }

        return { width: newWidth, height: newHeight };
      });
    };

    const handleMouseUp = () => {
      setIsResizing(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  // Quick start prompts - conversion-focused, pushing toward free trial
  const quickStartPrompts = [
    { icon: '🚀', text: 'I want to try BitsBound free' },
    { icon: '📄', text: 'I have a contract I need to review right now' },
    { icon: '⚡', text: 'Show me how fast I can get started' },
    { icon: '💡', text: 'What makes BitsBound different?' },
  ];

  const handleQuickStart = (promptText: string) => {
    setInputValue(promptText);
    inputRef.current?.focus();
  };

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    messagesEndRef.current?.scrollIntoView({ behavior: isMobile ? 'auto' : 'smooth' });
  }, [messages]);

  // Focus input on mount (desktop only)
  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    if (!isMobile) {
      inputRef.current?.focus();
    }
  }, []);

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedInput = inputValue.trim();
    if (!trimmedInput || isLoading) return;

    setError(null);

    // Add user message immediately
    const userMessage: Message = {
      id: `user_${Date.now()}`,
      role: 'user',
      content: trimmedInput,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      // AbortController with 5-minute timeout prevents UI freeze if API hangs
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 300000);

      const response = await fetch(`${API_BASE_URL}/api/sales-chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
        body: JSON.stringify({
          conversationId,
          message: trimmedInput,
          visitorInfo: {
            sessionId: getOrCreateSessionId(),
          },
        }),
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Failed to send message (${response.status})`);
      }

      let data: { conversationId?: string; messageId?: string; response?: string };
      try {
        data = await response.json();
      } catch {
        throw new Error('Invalid response from server');
      }

      // Update conversation ID
      if (data.conversationId) {
        setConversationId(data.conversationId);
      }

      // Add assistant message
      const assistantMessage: Message = {
        id: data.messageId || `assistant_${Date.now()}`,
        role: 'assistant',
        content: data.response || '',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, assistantMessage]);

    } catch (err: unknown) {
      const message = err instanceof Error && err.name === 'AbortError'
        ? 'Request timed out. Please try again.'
        : 'Sorry, there was an error sending your message. Please try again.';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FloatingWrapper>
      {/* Chat Modal - Only show when open or closing */}
      {(isOpen || isClosing) && (
        <ChatModal $width={dimensions.width} $height={dimensions.height} style={{ animation: isClosing ? `${slideOut} 0.25s ease-out forwards` : undefined }}>
          {/* Resize handles */}
          <ResizeHandle $position="top" onMouseDown={(e) => handleResizeStart(e, 'top')} />
          <ResizeHandle $position="left" onMouseDown={(e) => handleResizeStart(e, 'left')} />
          <ResizeHandle $position="top-left" onMouseDown={(e) => handleResizeStart(e, 'top-left')} />

          <ChatHeader>
            <HeaderContent>
              <HeaderIcon>🤖</HeaderIcon>
              <div>
                <HeaderTitle>BitsBound Sales</HeaderTitle>
                <HeaderSubtitle>AI Contract Automation</HeaderSubtitle>
              </div>
            </HeaderContent>
            <CloseButton onClick={handleClose} aria-label="Close chat">
              ✕
            </CloseButton>
          </ChatHeader>

          <MessagesContainer>
            {messages.length === 0 && (
              <WelcomeSection>
                <WelcomeHeader>
                  <ProductBadge>Try Free — No Credit Card</ProductBadge>
                  <WelcomeIcon>🚀</WelcomeIcon>
                  <WelcomeTitle>See BitsBound in Action</WelcomeTitle>
                  <WelcomeSubtitle>
                    Upload a contract. Get partner-level redlines in 45 minutes.
                    The best way to understand BitsBound is to try it yourself.
                  </WelcomeSubtitle>
                </WelcomeHeader>

                <InfoBox>
                  <InfoTitle>Why Legal Teams Love BitsBound</InfoTitle>
                  <InfoList>
                    <InfoItem>158+ AI analyzers attack your contract simultaneously</InfoItem>
                    <InfoItem>Real Track Changes — counterparties accept/reject in Word</InfoItem>
                    <InfoItem>45 minutes replaces 40+ hours of associate time</InfoItem>
                    <InfoItem>Free trial — see the quality before you pay</InfoItem>
                    <InfoItem>Start at just $30/month after trial</InfoItem>
                  </InfoList>
                </InfoBox>

                <QuickStartSection>
                  <QuickStartLabel>Quick start:</QuickStartLabel>
                  <QuickStartGrid>
                    {quickStartPrompts.map((prompt, index) => (
                      <QuickStartButton
                        key={index}
                        type="button"
                        onClick={() => handleQuickStart(prompt.text)}
                      >
                        <QuickStartIcon>{prompt.icon}</QuickStartIcon>
                        {prompt.text}
                      </QuickStartButton>
                    ))}
                  </QuickStartGrid>
                </QuickStartSection>
              </WelcomeSection>
            )}

            {messages.map((message) => (
              <Bitsbound_Kings_Home_Primary_SRC_Homepage_SubLevels_SalesReceptionist_Subpea
                key={message.id}
                message={message}
              />
            ))}

            {isLoading && (
              <TypingIndicator>
                <span />
                <span />
                <span />
              </TypingIndicator>
            )}

            {error && (
              <MessageBubble $isUser={false} style={{ background: 'rgba(239, 68, 68, 0.2)', color: BRAND.error, border: `1px solid ${BRAND.error}` }}>
                {error}
              </MessageBubble>
            )}

            <div ref={messagesEndRef} />
          </MessagesContainer>

          <InputContainer onSubmit={sendMessage}>
            <Input
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (inputValue.trim() && !isLoading) {
                    sendMessage(e);
                  }
                }
              }}
              placeholder="Ask about pricing, features, or request a demo..."
              disabled={isLoading}
              rows={1}
            />
            <SendButton type="submit" disabled={!inputValue.trim() || isLoading}>
              ➤
            </SendButton>
          </InputContainer>
        </ChatModal>
      )}

      {/* Floating Action Button with Tooltip */}
      <FABContainer $isOpen={isOpen}>
        <FABTooltip $isVisible={!isOpen}>
          Chat to learn more
        </FABTooltip>
        <FAB
          onClick={handleToggle}
          $isOpen={isOpen}
          aria-label={isOpen ? 'Close chat' : 'Open chat with BitsBound Sales'}
          aria-expanded={isOpen}
        >
          {isOpen ? '✕' : '💬'}
        </FAB>
      </FABContainer>
    </FloatingWrapper>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════
// O_3: UTILITY FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════

function getOrCreateSessionId(): string {
  const key = 'bitsbound_sales_session_id';
  let sessionId = sessionStorage.getItem(key);

  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Array.from(crypto.getRandomValues(new Uint8Array(5)), b => b.toString(36)).join('').slice(0, 9)}`;
    sessionStorage.setItem(key, sessionId);
  }

  return sessionId;
}

export default Bitsbound_Kings_Home_Primary_SRC_Homepage_SubLevels_SalesReceptionist_Subpod;